/**
 * 备注中的 Windows 本地盘 / 网络路径识别与打开（方案 A：自定义协议助手）。
 * 支持盘符路径（C:\～Z:\）与 UNC（\\10.10.3.210\…）。
 */

import { Modal } from 'antd';
import { $warning, $success, $info } from '@components/0_basics/message';

const PROTOCOL = 'ampfolder';
const STORAGE_KEY = 'amp_folder_helper_installed';

/** 盘符/UNC：遇空格即结束（备注格式为「路径 + 空格 + 文案」） */
const DRIVE_PATH_RE = /[A-Za-z]:\\[^<>"|?*\n\r\s]*/g;
const UNC_PATH_RE = /\\\\[^<>"|?*\n\r\s]+/g;
const TRAILING_TRIM_RE = /[,;，。；：、.!！？…]+$/;

/**
 * @param {string} raw
 * @returns {string}
 */
export function normalizeWindowsFolderPath(raw) {
    if (!raw || typeof raw !== 'string') return '';
    let path = raw.replace(/\//g, '\\').trim();
    path = path.replace(TRAILING_TRIM_RE, '');
    if (/^[A-Za-z]:$/.test(path)) {
        path = `${path}\\`;
    }
    return path;
}

/**
 * @param {string} raw
 * @returns {string}
 */
function cutAtSecondaryDriveRoot(raw) {
    if (!raw) return raw;
    const driveRe = /[A-Za-z]:\\/g;
    let m;
    let seen = 0;
    let cutAt = raw.length;
    while ((m = driveRe.exec(raw)) !== null) {
        seen += 1;
        if (seen > 1) {
            cutAt = m.index;
            break;
        }
    }
    if (!raw.startsWith('\\\\')) {
        const uncIdx = raw.indexOf('\\\\', 1);
        if (uncIdx > 0) cutAt = Math.min(cutAt, uncIdx);
    }
    let sliced = raw.slice(0, cutAt);
    if (cutAt < raw.length) {
        sliced = sliced.replace(/[\s\u4e00-\u9fff]+$/u, '');
    }
    return sliced.replace(TRAILING_TRIM_RE, '');
}

/**
 * @param {string} text
 * @returns {{ path: string, start: number, end: number, raw: string }[]}
 */
export function extractWindowsFolderPaths(text) {
    if (!text || typeof text !== 'string') return [];
    const matches = [];
    const collect = (re) => {
        re.lastIndex = 0;
        let m;
        while ((m = re.exec(text)) !== null) {
            const refinedRaw = cutAtSecondaryDriveRoot(m[0]);
            const path = normalizeWindowsFolderPath(refinedRaw);
            if (!path) {
                if (re.lastIndex === m.index) re.lastIndex += 1;
                continue;
            }
            matches.push({
                path,
                start: m.index,
                end: m.index + refinedRaw.length,
                raw: refinedRaw
            });
            re.lastIndex = m.index + Math.max(refinedRaw.length, 1);
        }
    };
    collect(DRIVE_PATH_RE);
    collect(UNC_PATH_RE);
    matches.sort((a, b) => a.start - b.start || b.end - a.start);
    const merged = [];
    let lastEnd = -1;
    for (const item of matches) {
        if (item.start < lastEnd) continue;
        merged.push(item);
        lastEnd = item.end;
    }
    return merged;
}

function encodePathForProtocol(folderPath) {
    return window.btoa(unescape(encodeURIComponent(normalizeWindowsFolderPath(folderPath))));
}

function buildProtocolUrl(folderPath) {
    return `${PROTOCOL}://open/${encodePathForProtocol(folderPath)}`;
}

function invokeProtocol(url) {
    const a = document.createElement('a');
    a.href = url;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

/**
 * 下载一键安装脚本（写入当前站点 origin，部署后他人可从同一站点安装）。
 */
export function downloadAmpFolderInstaller() {
    const origin = window.location.origin;
    const content = [
        '@echo off',
        'chcp 65001 >nul',
        'echo [AMP] Installing folder opener...',
        `powershell -NoProfile -ExecutionPolicy Bypass -Command " $ErrorActionPreference='Stop'; $base='${origin}'; $tmp=Join-Path $env:TEMP 'amp-folder-install.ps1'; Invoke-WebRequest -UseBasicParsing ($base+'/amp-open-folder/install.ps1') -OutFile $tmp; & $tmp -BaseUrl $base "`,
        'if errorlevel 1 (',
        '  echo Install failed.',
        '  pause',
        '  exit /b 1',
        ')',
        'echo.',
        'echo Install OK. Close this window, return to browser,',
        'echo allow ampfolder when prompted, then click the path again.',
        'pause'
    ].join('\r\n');

    const blob = new Blob([content], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'AMP-安装文件夹打开助手.bat';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

function showInstallModal() {
    Modal.confirm({
        title: '安装文件夹打开助手（仅需一次）',
        content:
            '浏览器无法直接打开本机/网络盘路径。请先安装助手：点击「立即安装」下载脚本并运行，完成后回到页面重新点击路径即可。',
        okText: '立即安装',
        cancelText: '取消',
        onOk: () => {
            downloadAmpFolderInstaller();
            try {
                localStorage.setItem(STORAGE_KEY, 'pending');
            } catch (e) {
                // ignore
            }
            $info('已开始下载安装脚本，请运行后刷新页面再点路径');
        }
    });
}

/**
 * 尝试唤起协议；若本机未安装助手则提示安装。
 * @param {string} folderPath
 * @returns {Promise<void>}
 */
function openViaAmpFolderProtocol(folderPath) {
    const url = buildProtocolUrl(folderPath);
    return new Promise((resolve) => {
        let settled = false;
        const finish = (opened) => {
            if (settled) return;
            settled = true;
            window.removeEventListener('blur', onBlur);
            resolve(opened);
        };
        const onBlur = () => {
            try {
                localStorage.setItem(STORAGE_KEY, '1');
            } catch (e) {
                // ignore
            }
            finish(true);
        };
        window.addEventListener('blur', onBlur);
        try {
            invokeProtocol(url);
        } catch (e) {
            finish(false);
            return;
        }
        setTimeout(() => finish(false), 1600);
    });
}

function createActiveX(progId) {
    try {
        // eslint-disable-next-line no-undef
        if (typeof ActiveXObject !== 'undefined') {
            // eslint-disable-next-line no-undef, new-cap
            return new ActiveXObject(progId);
        }
    } catch (e) {
        // ignore
    }
    return null;
}

function tryOpenViaActiveX(folderPath) {
    const path = normalizeWindowsFolderPath(folderPath);
    const fso = createActiveX('Scripting.FileSystemObject');
    if (!fso) return false;
    try {
        const shell = createActiveX('Shell.Application');
        if (!shell) return false;

        const isDriveRoot =
            /^[A-Za-z]:\\?$/.test(path.replace(/\\+$/, '') + '\\') ||
            /^[A-Za-z]:\\$/.test(path);
        const driveLetter = /^[A-Za-z]:/.test(path) ? path[0] : '';

        if (isDriveRoot) {
            if (!fso.DriveExists(`${driveLetter}:`)) {
                $warning('本机没有对应的路径');
                return true;
            }
            shell.Explore(`${driveLetter}:\\`);
            return true;
        }

        if (fso.FolderExists(path) || fso.FileExists(path)) {
            shell.Explore(path);
            return true;
        }

        // 子路径不存在：有盘符则回退打开盘符根（如 F:\）
        if (driveLetter && fso.DriveExists(`${driveLetter}:`)) {
            shell.Explore(`${driveLetter}:\\`);
            return true;
        }

        $warning('本机没有对应的路径');
        return true;
    } catch (e) {
        // ignore
    }
    return false;
}

/**
 * 点击备注中的文件夹路径。
 * @param {string} folderPath
 * @param {MouseEvent} [event]
 * @returns {Promise<void>}
 */
export async function openLocalFolderPath(folderPath, event) {
    if (event) {
        event.preventDefault();
        event.stopPropagation();
    }
    const path = normalizeWindowsFolderPath(folderPath);
    if (!path) return;

    if (tryOpenViaActiveX(path)) return;

    const opened = await openViaAmpFolderProtocol(path);
    if (opened) return;

    let flag = '';
    try {
        flag = localStorage.getItem(STORAGE_KEY) || '';
    } catch (e) {
        // ignore
    }
    if (flag === '1') {
        // 已装过但仍未失焦：可能路径不存在（助手已弹窗），或浏览器拦截了协议
        $warning('若未弹出文件夹，请确认本机路径存在，或允许浏览器打开 ampfolder');
        return;
    }
    showInstallModal();
}

export function markAmpFolderHelperInstalled() {
    try {
        localStorage.setItem(STORAGE_KEY, '1');
        $success('已标记助手可用，请再次点击路径');
    } catch (e) {
        // ignore
    }
}
